import { FloatingFocusManager, FloatingPortal, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react'
import { IconX } from '@tabler/icons-react'
import { AnimatePresence, motion } from 'motion/react'
import { ReactNode } from 'react'

import { AstraGlassCard } from '../AstraGlassCard/AstraGlassCard'
import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraHeading } from '../Typography/AstraHeading/AstraHeading'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type Alignment = 'top' | 'center'

export type AstraDialogProps = {
    children: ReactNode
    className?: string
    isOpen: boolean
    /**
     * If given, this will render instead of the title (and potentially afterTitle).
     * However title is still used for accessibility purposes.
     */
    header?: ReactNode
    title: string
    /**
     * If false, hides the header bar (close button, title, afterTitle).
     * The title is still used for accessibility (sr-only heading).
     */
    showHeader?: boolean
    /**
     * Overlay is always rendered, showOverlay just decides whether
     * make it visible or not
     */
    showOverlay?: boolean
    noPadding?: boolean
    afterTitle?: ReactNode
    onCloseDialog?: () => void
    /**
     * If true, the dialog will be modal and prevent interactions with the rest of the page
     *  THIS WILL ALSO BREAK SOME KEYBOARD ACCESSIBILITY and form inputs
     */
    isModal?: boolean
    align?: Alignment
    'data-test'?: string
    footer?: ReactNode
    disableOutsideClick?: boolean
    /**
     * If true, disables overflow scrolling on the content area.
     * Useful when dialog contains absolutely positioned elements (like Select dropdowns) that need to escape the content area.
     */
    disableOverflow?: boolean
}

// Generic dialog component that can be used to display any content in a dialog
export const AstraDialog = ({
    children,
    disableOutsideClick,
    isOpen,
    onCloseDialog,
    showOverlay = true,
    title,
    afterTitle,
    showHeader = true,
    noPadding,
    isModal = true,
    align = 'center',
    className,
    header,
    footer,
    disableOverflow = false,
    'data-test': dataTest = 'dialog',
}: AstraDialogProps) => {
    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: open => {
            if (!open) onCloseDialog?.()
        },
    })

    const dismiss = useDismiss(context, {
        outsidePress: !disableOutsideClick,
        outsidePressEvent: 'pointerdown',
    })
    const role = useRole(context, { role: 'dialog' })
    const { getFloatingProps } = useInteractions([dismiss, role])

    const contentClassName = astraTwMerge(noPadding ? 'p-0' : 'p-4', disableOverflow ? '' : 'overflow-y-auto')

    return (
        <AnimatePresence>
            {isOpen && (
                <FloatingPortal>
                    {isModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className={astraTwMerge('fixed inset-0 z-elevated', showOverlay ? 'bg-black/25' : '')}
                        />
                    )}
                    <FloatingFocusManager context={context} modal={isModal}>
                        <motion.div
                            transition={{ ease: 'easeOut' }}
                            initial={{ opacity: 0.5, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.12, ease: 'easeOut' } }}
                            ref={refs.setFloating}
                            data-test={dataTest}
                            className={astraTwMerge(
                                'fixed z-elevated flex h-fit max-h-screen w-fit flex-col rounded-xl',
                                align === 'top' ? 'inset-x-0 top-[15vh] mx-auto' : 'inset-0 m-auto',
                                className
                            )}
                            {...getFloatingProps({
                                'aria-describedby': undefined,
                                'aria-label': title,
                            })}
                        >
                            <AstraGlassCard>
                                <h2 className="sr-only">{title}</h2>
                                {showHeader && (
                                    <div className="flex justify-between border-b p-4">
                                        {header ? (
                                            header
                                        ) : (
                                            <div className="flex items-center gap-4">
                                                <AstraHeading as="h3">{title}</AstraHeading>
                                                {afterTitle}
                                            </div>
                                        )}
                                        <AstraButton onClick={onCloseDialog} icon={<AstraIcon icon={IconX} size="small" />} variant="ghost" />
                                    </div>
                                )}
                                <div className={contentClassName}>{children}</div>
                                {footer && <div className="border-t p-4">{footer}</div>}
                            </AstraGlassCard>
                        </motion.div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </AnimatePresence>
    )
}
